import React, { useState, useRef, useEffect } from 'react';
import './ReelCreator.css';
import { BACKEND_URL } from '../../services/Helper';
import { toast } from 'react-toastify';

const REEL_SERVICE = process.env.REACT_APP_REEL_SERVICE_URL || 'https://reel.nearbeee.in';

const TEMPLATES = [
  { id: 'restaurant', label: 'Restaurant', emoji: '🍽️', color: '#F97316', desc: 'Cafes, dhabas, restaurants' },
  { id: 'salon',      label: 'Salon',      emoji: '💅', color: '#EC4899', desc: 'Salons, spas, parlours' },
  { id: 'gym',        label: 'Gym',        emoji: '💪', color: '#1D4ED8', desc: 'Gyms, yoga, fitness' },
  { id: 'kirana',     label: 'Kirana',     emoji: '🛒', color: '#10B981', desc: 'Grocery, hardware, stores' },
  { id: 'generic',    label: 'Generic',    emoji: '🏪', color: '#7C3AED', desc: 'Any other business' },
];

export default function ReelCreator({ onClose, onReelSaved, shopToken, shopData }) {
  const [step, setStep] = useState(1);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({ offerText: '', tagline: '', address: shopData?.address || '' });
  const [slideIndex, setSlideIndex] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [reelUrl, setReelUrl] = useState(null);
  const fileInputRef = useRef();

  const readyPhotos = photos.filter(p => p.url && !p.uploading);

  // Auto-advance slideshow
  useEffect(() => {
    if (readyPhotos.length < 2) return;
    const t = setInterval(() => setSlideIndex(i => (i + 1) % readyPhotos.length), 2200);
    return () => clearInterval(t);
  }, [readyPhotos.length]);

  const uploadPhoto = async (file) => {
    const tempId = `${Date.now()}-${Math.random()}`;
    setPhotos(prev => [...prev, { tempId, url: null, uploading: true }]);
    try {
      const data = new FormData();
      data.append('file', file);
      data.append('upload_preset', process.env.REACT_APP_CLOUDINARY_PRESET);
      data.append('cloud_name', process.env.REACT_APP_CLOUDINARY_CLOUD);
      const res = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD}/image/upload`,
        { method: 'POST', body: data }
      );
      const json = await res.json();
      if (!json.secure_url) throw new Error('No URL returned');
      setPhotos(prev => prev.map(p => p.tempId === tempId ? { tempId, url: json.secure_url, uploading: false } : p));
    } catch {
      setPhotos(prev => prev.filter(p => p.tempId !== tempId));
      toast.error('Photo upload failed');
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const currentReady = photos.filter(p => p.url).length;
    files.slice(0, 8 - currentReady).forEach(uploadPhoto);
    e.target.value = '';
  };

  const removePhoto = (tempId) => setPhotos(prev => prev.filter(p => p.tempId !== tempId));

  const generateReel = async () => {
    if (readyPhotos.length < 2) { toast.error('Upload at least 2 photos'); return; }
    setGenerating(true);
    setReelUrl(null);
    try {
      const res = await fetch(`${REEL_SERVICE}/reel-api/reel/render`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${shopToken}` },
        body: JSON.stringify({
          shopId: shopData?._id,
          shopName: shopData?.shopname,
          city: shopData?.address || 'India',
          templateId: selectedTemplate,
          imageUrls: readyPhotos.map(p => p.url),
          musicUrl: null,
          caption: [form.tagline, form.offerText].filter(Boolean).join(' | '),
          offerText: form.offerText,
          tagline: form.tagline,
          address: form.address,
        }),
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      setReelUrl(data.reelUrl);
      toast.success('Reel generated! You can now download or save it.');
    } catch {
      toast.error('Reel service unavailable. Your photos are ready — use the slideshow preview for now.');
    } finally {
      setGenerating(false);
    }
  };

  const saveReelLink = async (url) => {
    try {
      const res = await fetch(`${BACKEND_URL}/nearbe/updatereellink`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: shopToken },
        body: JSON.stringify({ reelLink: url }),
      });
      if (res.ok) {
        toast.success('Reel saved to your shop page!');
        onReelSaved && onReelSaved(url);
      } else {
        toast.error('Could not save reel link');
      }
    } catch {
      toast.error('Something went wrong');
    }
  };

  const STEP_LABELS = ['Template', 'Photos', 'Customize', 'Preview'];

  return (
    <div className="rc-overlay" onClick={onClose}>
      <div className="rc-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="rc-header">
          <span className="rc-title">🎬 Create Reel</span>
          <div className="rc-steps">
            {STEP_LABELS.map((label, i) => (
              <div key={label} className={`rc-step ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}>
                <span className="rc-step-num">{step > i + 1 ? '✓' : i + 1}</span>
                <span className="rc-step-label">{label}</span>
              </div>
            ))}
          </div>
          <button className="rc-close" onClick={onClose}>✕</button>
        </div>

        <div className="rc-body">

          {/* ── Step 1: Template ── */}
          {step === 1 && (
            <div className="rc-step-content">
              <h3>Choose a Template</h3>
              <p className="rc-subtitle">Pick the style that best matches your business</p>
              <div className="rc-templates">
                {TEMPLATES.map(t => (
                  <div
                    key={t.id}
                    className={`rc-template-card ${selectedTemplate === t.id ? 'selected' : ''}`}
                    style={{ '--tc': t.color }}
                    onClick={() => setSelectedTemplate(t.id)}
                  >
                    <div className="rc-template-emoji">{t.emoji}</div>
                    <div className="rc-template-name">{t.label}</div>
                    <div className="rc-template-desc">{t.desc}</div>
                    {selectedTemplate === t.id && <div className="rc-template-check">✓</div>}
                  </div>
                ))}
              </div>
              <div className="rc-footer">
                <div />
                <button className="rc-btn-primary" disabled={!selectedTemplate} onClick={() => setStep(2)}>
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: Photos ── */}
          {step === 2 && (
            <div className="rc-step-content">
              <h3>Upload Photos</h3>
              <p className="rc-subtitle">Add 2–8 product or shop photos (portrait/square work best for reels)</p>
              <div className="rc-photo-grid">
                {photos.map(p => (
                  <div key={p.tempId} className="rc-photo-thumb">
                    {p.uploading ? (
                      <div className="rc-photo-loading">⏳</div>
                    ) : (
                      <>
                        <img src={p.url} alt="" />
                        <button className="rc-photo-remove" onClick={() => removePhoto(p.tempId)}>✕</button>
                      </>
                    )}
                  </div>
                ))}
                {photos.length < 8 && (
                  <div className="rc-photo-add" onClick={() => fileInputRef.current.click()}>
                    <span className="rc-add-plus">+</span>
                    <span className="rc-add-label">Add photo</span>
                  </div>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFileChange} />
              <p className="rc-hint">
                {readyPhotos.length} / 8 photos
                {photos.some(p => p.uploading) ? ' · Uploading…' : ' · Ready'}
              </p>
              <div className="rc-footer">
                <button className="rc-btn-ghost" onClick={() => setStep(1)}>← Back</button>
                <button className="rc-btn-primary" disabled={readyPhotos.length < 2} onClick={() => setStep(3)}>
                  Next →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Customize ── */}
          {step === 3 && (
            <div className="rc-step-content">
              <h3>Add Your Text</h3>
              <p className="rc-subtitle">All fields are optional — add what's relevant</p>
              <div className="rc-form">
                <label className="rc-label">
                  Offer / Discount Text
                  <input className="rc-input" placeholder='"20% OFF today only!"' value={form.offerText} onChange={e => setForm(f => ({ ...f, offerText: e.target.value }))} />
                </label>
                <label className="rc-label">
                  Tagline
                  <input className="rc-input" placeholder='"Best biryani in town"' value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} />
                </label>
                <label className="rc-label">
                  Address / Area
                  <input className="rc-input" placeholder='"Gandhi Nagar, Gwalior"' value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                </label>
              </div>
              <div className="rc-footer">
                <button className="rc-btn-ghost" onClick={() => setStep(2)}>← Back</button>
                <button className="rc-btn-primary" onClick={() => { setStep(4); setSlideIndex(0); }}>
                  Preview →
                </button>
              </div>
            </div>
          )}

          {/* ── Step 4: Preview & Generate ── */}
          {step === 4 && (
            <div className="rc-step-content rc-preview-layout">

              {/* Left — slideshow preview */}
              <div className="rc-preview-left">
                <div className="rc-slideshow">
                  {readyPhotos.map((p, i) => (
                    <img key={p.url} src={p.url} alt="" className={`rc-slide ${i === slideIndex ? 'active' : ''}`} />
                  ))}
                  <div className="rc-slide-overlay">
                    {form.tagline  && <div className="rc-slide-tagline">{form.tagline}</div>}
                    {form.offerText && <div className="rc-slide-offer">{form.offerText}</div>}
                    {form.address  && <div className="rc-slide-address">📍 {form.address}</div>}
                  </div>
                  <div className="rc-slide-dots">
                    {readyPhotos.map((_, i) => (
                      <div key={i} className={`rc-dot ${i === slideIndex ? 'active' : ''}`} />
                    ))}
                  </div>
                </div>
                <p className="rc-slideshow-note">Live preview — auto-advances every 2s</p>

                {reelUrl && (
                  <div className="rc-video-result">
                    <video src={reelUrl} controls style={{ width: '100%', borderRadius: 12 }} />
                    <div className="rc-video-actions">
                      <a href={reelUrl} download="nearbeee-reel.mp4" className="rc-btn-download">⬇ Download</a>
                      <button className="rc-btn-save" onClick={() => saveReelLink(reelUrl)}>💾 Save as Shop Reel</button>
                    </div>
                  </div>
                )}
              </div>

              {/* Right — summary + generate */}
              <div className="rc-preview-right">
                <div className="rc-summary">
                  <div className="rc-summary-title">Reel Summary</div>
                  <div className="rc-summary-row"><span>Template</span><strong>{TEMPLATES.find(t => t.id === selectedTemplate)?.emoji} {TEMPLATES.find(t => t.id === selectedTemplate)?.label}</strong></div>
                  <div className="rc-summary-row"><span>Photos</span><strong>{readyPhotos.length}</strong></div>
                  {form.offerText && <div className="rc-summary-row"><span>Offer</span><strong>{form.offerText}</strong></div>}
                  {form.tagline   && <div className="rc-summary-row"><span>Tagline</span><strong>{form.tagline}</strong></div>}
                  {form.address   && <div className="rc-summary-row"><span>Area</span><strong>{form.address}</strong></div>}
                </div>

                <button className="rc-btn-generate" onClick={generateReel} disabled={generating}>
                  {generating ? '⏳ Generating… (~30s)' : '🎬 Generate Video Reel'}
                </button>
                <p className="rc-generate-note">
                  AI composes your photos into a 30-second MP4 reel using the reel service.
                  Requires <code>reel.nearbeee.in</code> to be deployed.
                </p>

                {!reelUrl && (
                  <div className="rc-manual-save">
                    <p className="rc-manual-label">Or save your best photo as the shop reel link:</p>
                    <button className="rc-btn-manual" onClick={() => saveReelLink(readyPhotos[0]?.url)}>
                      📸 Use First Photo as Reel
                    </button>
                  </div>
                )}
              </div>

              <div className="rc-footer rc-footer-span">
                <button className="rc-btn-ghost" onClick={() => setStep(3)}>← Back</button>
                <div />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
